import { ChangeDetectionStrategy, Component, OnInit, WritableSignal, effect, ElementRef, ViewChild, AfterViewInit, signal, OnDestroy } from '@angular/core';
import { UiGlMail } from 'src/app/api/types/mc-dashboard';
import { DashboardDataService } from '../../../services/dashboard-data.service';
import { BaseDashboardComponent } from '../base-dashboard.component';

@Component({
  selector: 'app-gl-mail',
  templateUrl: './gl-mail.component.html',
  styleUrls: ['./gl-mail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GlMailComponent extends BaseDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  messages: WritableSignal<UiGlMail[]> = this.createSignal<UiGlMail[]>([]);
  
  @ViewChild('glMailCard', { static: false }) glMailCard!: ElementRef;
  
  // Dynamic height calculation for the scrolling container
  scrollHeight = signal<string>('400px');
  private resizeObserver?: ResizeObserver;

  constructor(
    private dashboardDataService: DashboardDataService
  ) {
    super();
    
    // Use effect to watch signals - ensure proper loading state management
    effect(() => {
      const data = this.dashboardDataService.glMail();
      const isLoading = this.dashboardDataService.componentLoadingState().glMail;
      
      // Only update messages if data is actually loaded (not null/undefined)
      if (data !== null && data !== undefined) {
        this.messages.set(data);
        this.setLoading(false);
        
        // Recalculate height after messages are loaded
        setTimeout(() => {
          this.calculateScrollHeight();
        }, 100);
      } else if (!isLoading) {
        // If not loading and no data, show empty state
        this.messages.set([]);
        this.setLoading(false);
        
        // Recalculate height for empty state
        setTimeout(() => {
          this.calculateScrollHeight();
        }, 100);
      }
    });
  }

  ngOnInit(): void {
    this.setLoading(true);
    // Initialize data loading
    this.dashboardDataService.initializeData();
  }

  ngAfterViewInit(): void {
    // Calculate initial height after view is initialized
    setTimeout(() => {
      this.calculateScrollHeight();
      this.setupHeightObserver();
    }, 300);
  }

  override ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Calculate the height for the scrollPanel to fit within the card
   */
  private calculateScrollHeight(): void {
    if (!this.glMailCard?.nativeElement) return;

    const cardElement = this.glMailCard.nativeElement;
    const cardHeight = cardElement.offsetHeight;
    const headerHeight = cardElement.querySelector('.p-card-header')?.offsetHeight || 0;
    const padding = 32; // Account for card padding
    
    // Calculate available height for the scrollPanel
    const availableHeight = Math.max(400, cardHeight - headerHeight - padding);
    
    this.scrollHeight.set(`${availableHeight}px`);
  }

  /**
   * Set up observer to watch for height changes and adjust scrollPanel accordingly
   */
  private setupHeightObserver(): void {
    if (!this.glMailCard?.nativeElement) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      // Debounce height recalculation to avoid excessive updates
      clearTimeout((this as any)._heightTimeout);
      (this as any)._heightTimeout = setTimeout(() => {
        this.calculateScrollHeight();
      }, 150);
    });

    this.resizeObserver.observe(this.glMailCard.nativeElement);
    
    // Also observe the parent container to catch layout changes
    const parentContainer = this.glMailCard.nativeElement.closest('.flex.md\\:flex-row');
    if (parentContainer) {
      this.resizeObserver.observe(parentContainer);
    }
  }

  /**
   * Get the current scroll height for the template
   */
  getScrollPanelHeight(): string {
    return this.scrollHeight();
  }
} 